/** Pure utility functions shared across Chat sub-components. */

import type { Message } from '@hooks/useChat';

export const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

export const formatTime = (date: Date | string): string =>
  new Date(date).toLocaleTimeString('en-US', TIME_FORMAT);

/** Send time of the user message that opened the turn message `idx` belongs to. */
export const turnStartedAt = (messages: Message[], idx: number): Date | string | undefined => {
  for (let i = idx - 1; i >= 0; i--) {
    if (messages[i].role === 'user') return messages[i].timestamp;
  }
  return undefined;
};

/** An assistant turn is stored as several rows, so `timestamp` is when it started. */
export const messageTime = (msg: Message): Date | string =>
  msg.role === 'assistant' ? msg.completedAt ?? msg.timestamp : msg.timestamp;

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Support reference the AI service appends to a sanitised failure message. */
const SUPPORT_TRACE_PATTERN = /\btrace=([0-9a-z]+)/i;

export const extractSupportTrace = (errorMessage: string): string | undefined =>
  SUPPORT_TRACE_PATTERN.exec(errorMessage)?.[1];

export const getErrorMessageKey = (errorMessage: string): string => {
  const lower = errorMessage.toLowerCase();
  // Checked first so a sanitised provider failure keeps its support reference
  // instead of being keyword-matched into an unrelated message.
  if (SUPPORT_TRACE_PATTERN.test(errorMessage)) return 'chat.error.turnFailed';
  if (lower.includes('session') || lower.includes('expired')) return 'chat.error.sessionExpired';
  if (lower.includes('authentication') || lower.includes('log in')) return 'chat.error.authenticationRequired';
  if (lower.includes('connection') || lower.includes('network')) return 'chat.error.connectionFailed';
  if (lower.includes('could not be uploaded')) return 'chat.error.imageUploadBlocked';
  if (lower.includes('file too large') || lower.includes('upload size')) return 'chat.error.uploadSizeExceeded';
  if (lower.includes('maximum concurrent conversations') || lower.includes('conversation limit')) {
    return 'chat.error.conversationLimit';
  }
  if (lower.includes('request') || lower.includes('http')) return 'chat.error.requestFailed';
  return 'chat.error.unknown';
};

// Re-export from the shared utility module so existing imports keep working.
export { filterFiles } from '@utils/files';

/** Watch for DOM mutations in a container and restore scroll position after content is inserted. */
export const preserveScrollAfterLoad = (
  container: HTMLElement,
  prevHeight: number,
  prevTop: number,
): void => {
  const obs = new MutationObserver(() => {
    requestAnimationFrame(() => {
      container.scrollTop = prevTop + (container.scrollHeight - prevHeight);
    });
    obs.disconnect();
  });
  obs.observe(container, { childList: true, subtree: true });
};
